import React, { SyntheticEvent, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { classes } from '../styles';
import { sharedClasses } from '../../../Components/CustomUIElements/sharedClasses';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FilterOptionsState,
  Modal,
  TextField
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import SelectedApplicantsWithTags from '../SelectedApplicantsWithTags';

const CREATE_TEXT = 'Create "';

interface ManageTagsProps {
  jobId: string | number;
  setSnackbar: (snackbar: { message: string; state: 'success' | 'error' | 'warning' }) => void;
  modalsOpen: { manageTags: boolean; [key: string]: boolean };
  setModalsOpen: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  globalSelected: Array<{ id: number; tag_list: string[] }>;
  handleDeselectCandidate: (id: number) => void;
  filters: any;
}

export const ManageTags = ({
  setSnackbar,
  modalsOpen,
  setModalsOpen,
  globalSelected,
  handleDeselectCandidate,
  filters
}: ManageTagsProps) => {
  const queryClient = useQueryClient();
  const tag_list =
    globalSelected.length > 0
      ? globalSelected.reduce((commonTags, app, index) => {
          // For the first app, use its tags as the initial common set
          if (index === 0) return [...app.tag_list];
          // For subsequent apps, keep only tags that exist in both the common set and the current app
          return commonTags.filter((tag) => app.tag_list.includes(tag));
        }, [] as string[])
      : [];
  const [tags, setTags] = useState<string[]>(tag_list);
  const [removedTags, setRemovedTags] = useState<string[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  const { data: entityTags, isLoading } = useQuery({
    queryKey: ['entityTags'],
    queryFn: async () => {
      const response = await fetch(`/api/v4/tags?simple=true`);
      return await response.json();
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });

  const handleClose = () => {
    setModalsOpen((prev) => ({ ...prev, manageTags: false }));
  };

  const { mutate: updateTags, isLoading: isSending } = useMutation({
    mutationFn: async () => {
      // Get the IDs of the selected applications
      const applicationIds = globalSelected.map((app) => app.id);

      // Send the request to update tags
      const response = await fetch('/api/v4/tags/bulk_update', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token':
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          application_ids: applicationIds,
          tags: tags
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update tags');
      }

      return response.json();
    },
    onSuccess: (data) => {
      setSnackbar({
        message: data.message || 'Tags updated successfully',
        state: 'success'
      });
      handleClose();

      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries(['applications']);
      queryClient.invalidateQueries(['candidates']);

      // Refresh the page to update the Application table with the newest data
      window.location.reload();
    },
    onError: (error: Error) => {
      setSnackbar({
        message: error.message || 'An error occurred while updating tags',
        state: 'error'
      });
    }
  });

  const handleSubmit = () => {
    updateTags();
  };

  const handleTagsChange = (_e: SyntheticEvent, value: string[]) => {
    const newTags = value.map((val) =>
      val.startsWith(CREATE_TEXT) ? val.slice(CREATE_TEXT.length, -1) : val
    );

    // Calculate removed tags by comparing with the original common tags
    const newRemovedTags = tag_list.filter((tag) => !newTags.includes(tag));

    setTags(newTags);
    setRemovedTags(newRemovedTags);
  };

  const filterOptions = (options: string[], state: FilterOptionsState<string>) => {
    const { inputValue } = state;

    const filtered = options.filter((option) =>
      option.toLowerCase().includes(inputValue.toLowerCase())
    );

    const isNew = !options.some((option) => option.toLowerCase() === inputValue.toLowerCase());

    if (inputValue && isNew) {
      filtered.push(`${CREATE_TEXT}${inputValue}"`);
    }

    return filtered;
  };

  return (
    <Modal
      open={modalsOpen.manageTags}
      onClose={handleClose}
      disableEnforceFocus
      aria-labelledby="manage-tags-modal"
    >
      <Box sx={classes.actionsModalReducedPadding}>
        <CloseIcon onClick={handleClose} sx={classes.closeIcon} />
        <Box id="manage-tags-title" sx={classes.actionModalHeader}>
          Manage Tags
        </Box>
        <Box
          sx={classes.modalContent}
          id="manage-tags-modal-content"
          aria-label="email Candidates Modal Content"
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <Autocomplete
              id="manage-tags-input"
              clearIcon={false}
              options={entityTags?.tags?.map((tag) => tag.value)}
              value={tags}
              filterOptions={filterOptions}
              onChange={handleTagsChange}
              freeSolo
              multiple
              sx={{ ...sharedClasses.formAutocomplete, '.MuiInputBase-root': { margin: 'unset' } }}
              renderTags={(value, props) =>
                value.map((option, index) => (
                  <Chip label={option} {...props({ index })} key={index} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  placeholder="Type your tag here..."
                  {...params}
                  sx={{ '& .MuiOutlinedInput-root': { padding: 0.5 } }}
                  ref={ref}
                />
              )}
            />
          )}
          <Box sx={{ marginLeft: '15px' }}>
            <h3>Candidates</h3>
            <SelectedApplicantsWithTags
              globalSelected={globalSelected}
              handleDeselectCandidate={handleDeselectCandidate}
              closeModalCallback={() => handleClose}
              filters={filters}
              modifiedTags={tags}
              removedTags={removedTags}
            />
          </Box>
        </Box>
        <Box sx={classes.modalFooter}>
          <Button
            id="close-manage-tags-button"
            sx={classes.modalCancelButton}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            id="apply-manage-tags-button"
            type="submit"
            sx={{ ...classes.modalSendButton, width: '71px', height: '41px' }}
            onClick={handleSubmit}
          >
            {isSending ? <CircularProgress size={20} color="inherit" /> : 'Apply'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
